/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { Input, InputGroup } from 'reactstrap';
import { ColumnInputProps, TableColumn } from '../InputInterface';
import {
  InputTableGroupPrePend,
  TableInputGroupAppend,
} from '../TableInputGroup';
import ValueSlider from '../../ValueSlider';
import { hasMinAndMaxColumnType } from '../../../../../../types/universalTracker';
import { HandleValueChangeProps } from '../../InputProps';
import { InputHelperText } from '../../InputHelperText';

const getValidatedValue = (value: string, column: TableColumn) => {
  if (!column.validation) {
    return value;
  }

  const numericValue = Number(value);

  const { max, min } = column.validation;
  if (max !== undefined && numericValue > max) {
    return max;
  }

  if (min !== undefined && numericValue < min) {
    return min;
  }

  return value;
};

export default function NumberInput(props: ColumnInputProps) {
  const {
    handleFocus,
    updateColumn,
    label,
    column,
    inputColumn,
    isDisabled,
    placeholder,
    inputMessage,
    universalTracker,
  } = props;

  const updateValue = (name: string, value: string) => updateColumn({
    code: name,
    value: value === '' ? undefined : getValidatedValue(value, column)
  });

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const { name, value } = e.target;
    return updateValue(name, value);
  };

  const value = inputColumn.value ?? '';

  const { required = true, max, min } = column.validation ?? {};
  // 0-100 treat as percentage slider
  const renderSlider = hasMinAndMaxColumnType(column.type, min, max);
  const disabled = isDisabled?.(props);
  const invalidMessage = inputMessage?.[column.code];
  const isInvalid = !!invalidMessage;

  if (renderSlider) {
    return (
      <div className={'mb-3'}>
        {label}
        <ValueSlider
          value={value}
          universalTracker={universalTracker}
          disabled={disabled}
          placeholder={placeholder}
          handleChange={({ value }: HandleValueChangeProps) => updateValue(column.code, value)}
          invalidMessage={invalidMessage}
          column={column}
          min={min}
          max={max}
        />
      </div>
    );
  }

  return <div className={'mb-3'}>
    {label}
    <InputGroup className='has-validation'>
      {InputTableGroupPrePend({ ...props, isInvalid })}
      <Input
        type='number'
        max={max}
        min={min}
        placeholder={placeholder}
        autoComplete='off'
        className={isInvalid ? '' : 'styled-input rounded-end'}
        id={column.code}
        name={column.code}
        value={value}
        aria-describedby='suffix'
        disabled={!!disabled}
        required={required}
        onFocus={handleFocus}
        onClick={handleFocus}
        invalid={isInvalid}
        onWheel={e => e.currentTarget.blur()}
        onChange={onChange} />
      {TableInputGroupAppend({ ...props, isInvalid })}
      <InputHelperText invalidMessage={invalidMessage} universalTracker={universalTracker} inputName={column.code} />
    </InputGroup>
  </div>;
}
